import React, {createContext, useContext, useEffect, useState} from "react";
import axios from "axios";
import moment from "moment";
import {Patient, PatientCreate} from "../Types";
import {AuthContext, MessageContext, MessageType, TourContext} from "../Contexts";

const patientsContextDefaultValues: {
  patients: Patient[];

  selectPatient: (clinicUserId: string) => void;
  passHistory: (history: any) => void;

  createPatient: (p: PatientCreate) => Promise<void>;
  editPatient: (patientID: String, newSettings: PatientCreate) => Promise<void>;
  archivePatient: (patient: Patient) => Promise<void>;
  restorePatient: (patient: Patient) => Promise<void>;

  loading: boolean;
} = { patients: [], selectPatient: () => {}, passHistory: () => {}, loading: true, createPatient: async () => {}, editPatient: async () => {}, archivePatient: async () => {}, restorePatient: async () => {}};

export const PatientsContext = createContext(patientsContextDefaultValues);

let fakePatientCount = 0
let newPatientClinicId: string | null = null

export const PatientsContextProvider: React.FC<{}> = ({ children }) => {
  const [patients, setPatients] = useState<Patient[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [history, setHistory] = useState<any>();

  const authContext = useContext(AuthContext);
  const tourContext = useContext(TourContext);
  const messageContext = useContext(MessageContext);

  useEffect(() => {
    fetchPatients();
  }, [authContext.user]);

  useEffect( () => {
    const newCard = document.getElementById("card_" + newPatientClinicId)
    if (newCard) {
      newCard?.scrollIntoView()
      newPatientClinicId = null
    }
  })

  const selectPatient = (clinicUserId) => {
    history?.push(`/patient/${clinicUserId}/measurements`)
    document.getElementById("card_" + clinicUserId)?.scrollIntoView()
  }

  const deselectPatient = () => {
    history?.push(`/patient`)
  }

  const fetchPatients = async () => {
    setLoading(true);
    if (authContext.user) {
      const result = await axios.get<Patient[]>("/vue_api/get_patients");
      if (result.data) {
        setPatients(
          result.data.map((p) => {
            let parsedSettings = null;
            let parsedGameSettings = null;
            try {
              if (p.settings) {
                parsedSettings = JSON.parse(
                  (p.settings as unknown) as string
                );
              }

              if (p.games_settings) {
                parsedGameSettings = JSON.parse(
                  (p.games_settings as unknown) as string
                );
              }
            } catch (e) {
              console.log(`failed to parse settings for ${p.user_name}`);
            }

            return {
              ...p,
              games_settings: parsedGameSettings,
              settings: parsedSettings,
            };
          }).sort((firstPatient, secondPatient) => firstPatient.user_name.localeCompare(secondPatient.user_name))
        );
      }
    } else {
      setPatients([]);
    }
    setLoading(false);
  };

  const createPatient = async (patientCreate: PatientCreate) => {
    const payload = {
      ...patientCreate,
      dob: moment(patientCreate.dob, "YYYY-MM-DD").unix() + "",
      p: authContext.user?.pass_hash,
      u: authContext.user?.user_name,
      doctor_id: authContext.user?.user_id
    };

    if (tourContext.openedTour == null) {
      axios.post("/vue_api/add_patient", payload)
        .then((response) => {
          if (response.data["success"] === "User created.") {
            fetchPatients()
            newPatientClinicId = response.data.new_clinic_user_id
            selectPatient(newPatientClinicId)
            messageContext.addMessage("Successfully create new user " + payload.user_name, MessageType.success)
          }
          else {
            messageContext.addMessage("Failed to create new user " + payload.user_name, MessageType.warn)
            console.log("failed to create user", response)
          }
      })
    } else {
      const fakeClinicID: string = ("fake" + fakePatientCount).toString()

      setPatients(
        patients.concat([{
          ...patients[0], ...{"user_name": patientCreate.user_name, "clinic_user_id": fakeClinicID, "user_id": "0", "is_active": "1"}
        }]).sort((firstPatient, secondPatient) => firstPatient.user_name.localeCompare(secondPatient.user_name)))
      fakePatientCount += 2

      newPatientClinicId = fakeClinicID
      selectPatient(newPatientClinicId)
      messageContext.addMessage("Successfully created new temporary user " + payload.user_name, MessageType.success)
    }

  };

  const editPatient = async (patientID: String, newSettings: PatientCreate) => {
    console.log("Edit patient", patientID, newSettings)
  }


    const archivePatient = async (patient: Patient) => {
    console.log("Archive patient", patient)
    patient.is_active = "0"
    setLoading(true);

    if (tourContext.openedTour === null) {
      axios.post("/vue_api/archive_patient", {clinic_user_id: patient.clinic_user_id})
        .then((response) => {
          if (response.data.success !== "archive of patient was successful") {
            patient.is_active = "1"
            console.log("Failed to restore patient", patient.clinic_user_id, response.data.success)
            messageContext.addMessage("Failed to archived user " + patient.user_name, MessageType.warn)

          } else {
            deselectPatient()
            messageContext.addMessage("Successfully archived user " + patient.user_name, MessageType.success)
          }
          setLoading(false);
      })
    } else {
      messageContext.addMessage("Temporarily archived user " + patient.user_name, MessageType.success)
    }
  }

  const restorePatient = async (patient: Patient) => {
    console.log("Restore patient", patient)
    patient.is_active = "1"
    setLoading(true);

    if (tourContext.openedTour === null) {
      axios.post("/vue_api/restore_patient", {clinic_user_id: patient.clinic_user_id})
        .then((response) => {
          if (response.data.success !== "restore of patient was successful") {
            patient.is_active = "0"
            console.log("Failed to restore patient", patient.clinic_user_id, response.data.success)
            messageContext.addMessage("Failed to restore user " + patient.user_name, MessageType.warn)
          } else {
            selectPatient(patient.clinic_user_id)
            messageContext.addMessage("Successfully restored user " + patient.user_name, MessageType.success)
          }
          setLoading(false);
      })
    } else {
      selectPatient(patient.clinic_user_id)
      messageContext.addMessage("Temporarily restored user " + patient.user_name, MessageType.success)
    }
  }

  return (
    <PatientsContext.Provider value={{patients: patients, loading: loading, createPatient: createPatient, editPatient: editPatient, archivePatient: archivePatient, restorePatient: restorePatient, selectPatient: selectPatient, passHistory: setHistory}}>
      {children}
    </PatientsContext.Provider>
  );
};
